import { createClient } from '@supabase/supabase-js';
import { toast } from 'react-hot-toast';

// Get environment variables
const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

// Validate Supabase configuration
const isSupabaseConfigured = () => {
  if (!supabaseUrl || !supabaseAnonKey) {
    return false;
  }

  try {
    // Test if URL is valid
    new URL(supabaseUrl);
    return true;
  } catch {
    return false;
  }
};

// Create client with proper error handling
let supabase;

try {
  if (isSupabaseConfigured()) {
    supabase = createClient(supabaseUrl, supabaseAnonKey, {
      auth: {
        persistSession: true,
        autoRefreshToken: true,
      }
    });
  } else {
    // Create a dummy client that will show appropriate errors
    supabase = {
      from: () => ({
        select: () => Promise.reject(new Error('Supabase not configured')),
        insert: () => Promise.reject(new Error('Supabase not configured')),
        update: () => Promise.reject(new Error('Supabase not configured')),
        delete: () => Promise.reject(new Error('Supabase not configured')),
      }),
      auth: {
        getSession: () => Promise.resolve({ data: { session: null }, error: null }),
        onAuthStateChange: () => ({ data: { subscription: { unsubscribe: () => {} } } }),
        signUp: () => Promise.reject(new Error('Supabase not configured')),
        signInWithPassword: () => Promise.reject(new Error('Supabase not configured')),
        signOut: () => Promise.reject(new Error('Supabase not configured')),
        resetPasswordForEmail: () => Promise.reject(new Error('Supabase not configured')),
      }
    };
  }
} catch (error) {
  console.error('Failed to initialize Supabase client:', error);
  throw error;
}

// Wrap Supabase queries with connection check
const withConnectionCheck = async (operation: () => Promise<any>) => {
  if (!isSupabaseConfigured()) {
    const error = new Error('Please connect to Supabase first');
    toast.error(error.message);
    return { data: null, error };
  }

  try {
    return await operation();
  } catch (error: any) {
    console.error('Supabase operation failed:', error);
    toast.error(error.message || 'Operation failed');
    return { data: null, error };
  }
};

// Enhanced Supabase client with connection checking
export const enhancedSupabase = {
  from: (table: string) => ({
    select: (query?: string) => 
      withConnectionCheck(() => supabase.from(table).select(query)),
    insert: (data: any) => 
      withConnectionCheck(() => supabase.from(table).insert(data)),
    update: (data: any) => 
      withConnectionCheck(() => supabase.from(table).update(data)),
    delete: () => 
      withConnectionCheck(() => supabase.from(table).delete()),
  }),
  auth: supabase.auth,
};

// Export the base client
export { supabase };

// Export configuration status
export const getSupabaseStatus = () => ({
  isConfigured: isSupabaseConfigured(),
  url: supabaseUrl,
  hasAnonymousKey: !!supabaseAnonKey
});